import { authStore } from '../stores/globalStores';
import constants from '@playcastdotio/GlobalConfig';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { profileState } from '../stores/lobbyStores';
import { toast } from 'sonner';
import { addObservabilityHeaders } from '@playcastdotio/Observability';
import { getPlayerCorrelationIds } from './playerObservability';

interface ApiResponse {
  success: boolean;
  error?: string;
  [key: string]: any;
}

interface ErrorResponseData {
  error?: string;
}

const ensureTrailingSlash = (url: string) => {
  if (url.endsWith('/')) return url;
  return url.concat('/');
};

export const send = async (
  method: string,
  url: string,
  data?: Record<string, any>,
  urlOverride?: string,
): Promise<ApiResponse | undefined> => {
  let accessToken = authStore.currentState.accessToken;
  if (!accessToken) {
    accessToken = await new Promise<string>((resolve, reject) => {
      const listener = authStore.addListener((state) => {
        if (state.accessToken) {
          authStore.removeListener(listener);
          resolve(state.accessToken);
        }
      });
      setTimeout(() => {
        reject(new Error('Timeout waiting for access token'));
      }, 10000);
    });
  }

  const headers: Record<string, string> = {
    Authorization: 'Bearer '.concat(accessToken),
    'Content-Type': 'application/json',
  };

  // Stamp hierarchical correlation headers so realtime-api tags the request.
  const ids = getPlayerCorrelationIds();
  addObservabilityHeaders(new Headers(), () => ids.machineId, () => ids.broadcastId).forEach(
    (value, key) => {
      headers[key] = value;
    },
  );

  const config: AxiosRequestConfig = {
    method,
    maxBodyLength: Infinity,
    url: ensureTrailingSlash(urlOverride ?? constants.REALTIME_API_BASE_URL).concat(url),
    headers,
  };

  if (['post', 'put', 'patch'].includes(method) && data) {
    config.data = JSON.stringify(data);
  }

  try {
    const response = await axios.request(config);
    return response.data;
  } catch (error: unknown) {
    const e = error as AxiosError<ErrorResponseData>;
    console.error('[API] Failed to post to API: ', e.code, config);
    return {
      success: false,
      error: e.response?.data?.error || 'Failed to send lobby announcement',
    };
  }
};

const isLocal = constants.VERSION.endsWith('-local') && constants.REALTIME_API_BASE_URL.includes('localhost');

let currentTwitchPromise: Promise<any> | null = null;
export const sendTwitchBot = async (invitationId: string) => {
  if (currentTwitchPromise) {
    return currentTwitchPromise;
  }
  currentTwitchPromise = new Promise((resolve, reject) => {
    const toastId = toast.loading('Sending lobby announcement...', {
      style: {
        color: '#ffffffaa',
      },
      duration: 3000,
    });
    send(
      'post',
      'twitch/events/lobby',
      {
        invitationId,
      },
      isLocal ? 'http://localhost:3200' : undefined,
    )
      .then((res) => {
        // Show toast with result and optional error message
        if (res?.success) {
          toast.success('Lobby announcement sent successfully', {
            id: toastId,
            style: {
              // Green color
              color: '#00aa00',
            },
          });
        } else {
          toast.error(res?.error || 'Failed to send lobby announcement', {
            id: toastId,
            style: {
              // Red color
              color: '#aa0000',
            },
          });
        }
        console.log(res);
        resolve(res);
        currentTwitchPromise = null;
      })
      .catch((error) => {
        const e = error as AxiosError<ErrorResponseData>;
        toast.error(e.response?.data?.error || 'Failed to send lobby announcement', {
          id: toastId,
          style: {
            color: '#aa0000',
          },
        });
        console.error(error);
        reject(error);
        currentTwitchPromise = null;
      });
  });
  return currentTwitchPromise;
};

let currentDiscordPromise: Promise<any> | null = null;
export const sendDiscordBot = async (invitationId: string) => {
  if (currentDiscordPromise) {
    return currentDiscordPromise;
  }
  currentDiscordPromise = new Promise((resolve, reject) => {
    const toastId = toast.loading('Sending lobby announcement...', {
      style: {
        color: '#ffffffaa',
      },
      duration: 3000,
    });
    const lobbyTitle = profileState.currentState.invitations.find((invitation) => invitation.id === invitationId)?.name;
    send(
      'post',
      'discord/events/lobby',
      {
        invitationId,
        title: lobbyTitle,
      },
      isLocal ? 'http://localhost:3100' : undefined,
    )
      .then((res) => {
        // Show toast with result and optional error message
        if (res?.success) {
          toast.success('Lobby announcement sent successfully', {
            id: toastId,
            style: {
              // Green color
              color: '#00aa00',
            },
          });
        } else {
          toast.error(res?.error || 'Failed to send lobby announcement', {
            id: toastId,
            style: {
              // Red color
              color: '#aa0000',
            },
          });
        }
        console.log(res);
        resolve(res);
        currentDiscordPromise = null;
      })
      .catch((error) => {
        const e = error as AxiosError<ErrorResponseData>;
        toast.error(e.response?.data?.error || 'Failed to send lobby announcement', {
          id: toastId,
          style: {
            color: '#aa0000',
          },
        });
        console.error(error);
        reject(error);
        currentDiscordPromise = null;
      });
  });
  return currentDiscordPromise;
};
